import { Inject, Injectable } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import * as moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { CapabilityIdEnum } from 'src/app/shared/capabilities-and-permissions';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { actionTypes } from './action-types.const';
import { dateRanges } from './date-ranges.const';
import { SearchCriteria } from './search-criteria.model';
import { statuses } from './statuses.const';

@Injectable({ providedIn: 'root' })
export class SearchService {
  parameters: SearchCriteria = this.initializeParameters();

  missingSearchCriteria = true;

  form: FormGroup = this.formBuilder.group({
    messageAttributes: this.formBuilder.group({
      receiptDate: [],
      accounts: [[]],
      statuses: [[]],
    }),
    messageContent: this.formBuilder.group({
      containingText: [],
      notContainingText: [],
    }),
    actions: this.formBuilder.group({
      date: [],
      types: [[]],
      processedBy: [[]],
      hideMessagesProcessedOnlyByBots: [],
    }),
  });

  sites: Choice[] = this.initializeSites();
  dateRanges: Choice[] = this.initializeDateRanges();
  actionDateRanges: Choice[] = this.initializeActionDateRanges();
  actionTypes: Choice[] = this.initializeActionTypes();
  statuses: Choice[] = this.initializeStatuses();
  advisors: Choice[] = this.initializeAdvisors();

  searchFiltersBS: BehaviorSubject<Map<string, { id: string; label: string; clearFn: any }>> = new BehaviorSubject(
    new Map()
  );

  constructor(
    private basicService: BasicService,
    private alcmeonContextService: AlcmeonContextService,
    private sitesService: SitesService,
    private formBuilder: FormBuilder,
    private customRouter: CustomRouter,
    @Inject('SiteFilterHelperFactory') private siteFilterHelperService: any
  ) {}

  retrieveTasksFromForm() {
    this.missingSearchCriteria = !this.searchFiltersBS.value.size;
    this.propagateChange();
  }

  private propagateChange() {
    let defaultParameters = '{ states: [], sites_ids: [], tasks_ids: [] }';
    try {
      if (this.parameters) {
        defaultParameters = JSON.stringify(this.parameters);
      }
    } catch (error) {
      defaultParameters = '{ states: [], sites_ids: [], tasks_ids: [] }';
    }
    localStorage.setItem(`${this.alcmeonContextService.companyId}-search-page-filters`, defaultParameters);

    this.searchFiltersBS.next(this.computeSearchFiltersMap());

    this.customRouter.reload('auth.alcmeon.search.tasks');
  }

  computeSearchFiltersMap() {
    const result: Map<string, { id: string; label: string; clearFn: any }> = new Map();

    const addItem = (value, controlName, translationSuffixKey, formGroupControlName, badge) => {
      result.set(controlName, {
        id: controlName,
        label: `${this.basicService.translate(`search/advanced-search/${translationSuffixKey}`)}: ${badge}`,
        clearFn: () => {
          this.form.get(formGroupControlName).reset();
          this.retrieveTasksFromForm();
        },
      });
    };

    const addItemFlag = (value, controlName, translationSuffixKey, formGroupControlName) => {
      if (value) {
        addItem(value, controlName, translationSuffixKey, formGroupControlName, value ? 1 : 0);
      }
    };

    const addItemArray = (value, controlName, translationSuffixKey, formGroupControlName) => {
      if ((value || []).length) {
        addItem(value, controlName, translationSuffixKey, formGroupControlName, value.length);
      }
    };

    addItemFlag(
      this.parameters.receiptDate,
      'receiptDate',
      'messages-attributes/receipt-date',
      'messageAttributes.receiptDate'
    );

    addItemArray(this.parameters.sites_ids, 'accounts', 'messages-attributes/accounts', 'messageAttributes.accounts');
    addItemArray(this.parameters.states, 'statuses', 'messages-attributes/statuses', 'messageAttributes.statuses');

    addItemFlag(
      this.parameters.message_includes,
      'containingText',
      'messages-content/containing-text',
      'messageContent.containingText'
    );
    addItemFlag(
      this.parameters.message_excludes,
      'notContainingText',
      'messages-content/not-containing-text',
      'messageContent.notContainingText'
    );

    addItemFlag(this.parameters.actionDate, 'actionDate', 'message-actions/action-date', 'actions.date');

    addItemArray(this.parameters.action_types, 'actionTypes', 'message-actions/action-types', 'actions.types');

    addItemArray(
      this.parameters.action_author_ids,
      'advisorsWhoProcessed',
      'message-actions/advisors-who-processed',
      'actions.processedBy'
    );

    return result;
  }

  initializeParameters(): SearchCriteria {
    return {
      since: moment().subtract(24, 'hours').toISOString(),
      until: moment().toISOString(),
      sites_ids: [],
      receiptDate: 'last-24-hours',
    };
  }

  initializeSites(): Choice[] {
    return this.siteFilterHelperService.filterSitesThatHaveCapability(
      this.sitesService.sitesV2,
      this.alcmeonContextService.self,
      CapabilityIdEnum.Answer
    );
  }

  initializeDateRanges(): Choice[] {
    return dateRanges.map(({ id, labelKey }) => ({
      id,
      label: this.basicService.translate(labelKey),
    }));
  }

  initializeActionDateRanges(): Choice[] {
    return [{ id: '', labelKey: null }].concat(dateRanges).map(({ id, labelKey }) => ({
      id,
      label: labelKey ? this.basicService.translate(labelKey) : '',
    }));
  }

  initializeActionTypes(): Choice[] {
    return actionTypes
      .map(({ id, labelKey }) => ({
        id,
        label: this.basicService.translate(labelKey),
      }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }

  initializeStatuses(): Choice[] {
    return statuses
      .map(status => ({
        id: status,
        label: this.basicService.translate(`task/state/${status}`),
      }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }

  initializeAdvisors(): Choice[] {
    return this.alcmeonContextService
      .getCompany()
      .users.filter(user => user.is_active && !user.is_bot)
      .map(({ id, realname }) => ({ id, label: realname }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }

  getDateRangeFromChoice(value: string) {
    let start = null;
    let end = null;
    switch (value) {
      case 'last-24-hours': {
        start = moment().subtract(24, 'hours');
        end = moment();
        break;
      }

      case 'last-week': {
        start = moment().subtract(7, 'days');
        end = moment();
        break;
      }
      case 'last-month': {
        start = moment().subtract(1, 'month');
        end = moment();
        break;
      }
      case 'last-6-month': {
        start = moment().subtract(6, 'months');
        end = moment();
        break;
      }
      case 'start-of-year': {
        start = moment().startOf('year');
        end = moment();
        break;
      }

      default:
        return null;
    }
    return { start, end };
  }
}
